import React from "react";
import { cn } from "../../lib/utils";
import type { MapViewerLegendSheetBodyProps } from "../../contracts/map-viewer";
import { MapLegendSwatch } from "../map-workspace/internal/MapLegendSwatch";
import { Separator } from "../../internal/primitives/separator";

export function MapViewerLegendSheetBody({
  title,
  description,
  sections,
  emptyMessage = "No legend items available",
  className,
}: MapViewerLegendSheetBodyProps) {
  return (
    <div className={cn("flex flex-col h-full bg-card", className)}>
      <header className="px-4 py-4 shrink-0 border-b border-border">
        <h2 className="text-sm font-bold mb-1">{title || "Legend"}</h2>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {sections.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-8 italic">
            {emptyMessage}
          </p>
        ) : (
          sections.map((section) => (
            <div key={section.id} className="space-y-3">
              <div className="flex flex-col">
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  {section.title}
                </h3>
                {section.description && (
                  <p className="text-[10px] text-muted-foreground/70">
                    {section.description}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                {section.entries.map((entry) => (
                  <div
                    key={entry.id}
                    className={cn(
                      "flex items-start gap-3 py-1",
                      entry.hidden && "opacity-40 grayscale"
                    )}
                  >
                    <MapLegendSwatch
                      className="mt-0.5 shrink-0"
                      label={entry.label}
                      color={entry.color}
                      representation={entry.representation}
                      iconToken={entry.iconToken}
                      iconPreviewUrl={entry.iconPreviewUrl}
                      iconSdf={entry.iconSdf}
                    />

                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-medium leading-none">
                        {entry.label}
                      </span>
                      {entry.description && (
                        <p className="mt-1 text-[10px] text-muted-foreground leading-tight">
                          {entry.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <Separator className="mt-4 opacity-50" />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
